/**
 * A finished session's recorded terminal.
 *
 * The transcript next door covers a session while it exists. `Manager.Kill` drops
 * the scrollback and the transcript and deliberately leaves the recording, so the
 * moment a session ends the transcript endpoint answers 404 and this is the only
 * history there is. Until it existed, the one artefact Kimene keeps on purpose
 * was the one nobody could read — while the console went on offering to make
 * them.
 *
 * Text, for the reason the transcript is text, and it is worth repeating here
 * because a contributor may open only this file. Replaying the recorded bytes
 * into a browser emulator was tried and produced unreadable output: tmux does not
 * describe a screen by printing it, it addresses the cursor absolutely — 90,692
 * absolute addressings against 5,388 line feeds in one measured 3.8 MB record —
 * so at any width but the original, every address lands in the wrong place. The
 * server renders it instead, through the same Screen that draws the live pane, and
 * what arrives here has no escape sequences in it at all.
 *
 * Three things it must say rather than imply, because each is a different reason
 * for a short answer: the geometry was not recorded, so this is a render at a
 * guess; the session drew on the alternate screen, so there is no history by
 * construction rather than because nothing happened; and redaction did not run, so
 * this is a materially different artefact from the one the operator was promised.
 */

import { useState } from 'react'
import clsx from 'clsx'

import { Button } from '@/components/primitives'
import { Notice } from '@/components/controls'
import { useT } from '@/i18n/context'
import { useWindowedLines } from './useWindowedLines'

const PAGE_LINES = 2000

interface RecordingWindow {
  lines: string[]
  from: number
  next: number
  first: number
  end: number
  screen: string[]
  redacted: boolean
  cols: number
  rows: number
  geometryKnown: boolean
  alternateScreen: boolean
  chunks: number
}

export function RecordingOverlay({
  sessionId,
  onClose,
  focusSearch = false,
}: {
  sessionId: string
  onClose: () => void
  /** Opened to search rather than to read. See TranscriptOverlay. */
  focusSearch?: boolean
}) {
  const t = useT()
  const [query, setQuery] = useState('')
  const { window: win, lines, loading, error, hasOlder, loadOlder, scrollRef } =
    useWindowedLines<RecordingWindow>(`/sessions/${sessionId}/recording`, PAGE_LINES)

  const matcher = query.trim().toLocaleLowerCase('tr')
  const matches = matcher
    ? lines.filter((l) => l.toLocaleLowerCase('tr').includes(matcher)).length
    : 0

  return (
    <div className="k-hist" role="dialog" aria-modal="false" aria-label={t('rec.title')}>
      <header className="k-hist__head">
        <div className="k-hist__ident">
          <span className="k-hist__title">{t('rec.title')}</span>
          <span className="k-hist__note">{t('rec.note')}</span>
        </div>
        <div className="k-hist__actions">
          <input
            type="search"
            className="k-hist__search"
            placeholder={t('terminal.search')}
            aria-label={t('terminal.search')}
            autoFocus={focusSearch}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation()
              if (e.key === 'Escape') onClose()
            }}
          />
          {matcher !== '' && (
            <span className="k-hist__busy k-mono">{t('terminal.historyMatches', { matches })}</span>
          )}
          {loading && <span className="k-hist__busy" role="status">{t('state.loading')}</span>}
          <Button size="sm" variant="ghost" onClick={onClose}>
            {t('action.close')}
          </Button>
        </div>
      </header>

      {error && <Notice tone="err">{error}</Notice>}

      {/* Each of these is a different reason the answer might be short, and each
          is one an operator would otherwise have to guess at. */}
      {win && !win.redacted && (
        <Notice tone="warn">{t('rec.notRedacted')}</Notice>
      )}
      {win && !win.geometryKnown && (
        <Notice tone="warn">{t('rec.geometryUnknown', { cols: win.cols, rows: win.rows })}</Notice>
      )}
      {win && win.alternateScreen && (
        <Notice tone="info">{t('rec.alternateScreen')}</Notice>
      )}

      {hasOlder && (
        <Button size="sm" variant="ghost" busy={loading} onClick={() => void loadOlder()}>
          {t('terminal.historyOlder')}
        </Button>
      )}

      <div className="k-hist__lines" ref={scrollRef} tabIndex={0}>
        {lines.map((line, i) => (
          <div
            key={win ? win.from + i : i}
            className={clsx(
              'k-hist__line',
              matcher !== '' &&
                line.toLocaleLowerCase('tr').includes(matcher) &&
                'k-hist__line--hit',
            )}
          >
            {line === '' ? ' ' : line}
          </div>
        ))}
        {win && win.screen.length > 0 && (
          <>
            {/* The last screen, kept apart from the history for the reason the
                transcript keeps the live one apart: a screen is not a set of
                lines that scrolled, and concatenating them invents rows that
                never did. */}
            <div className="k-hist__now">{t('rec.lastScreen')}</div>
            {win.screen.map((line, i) => (
              <div key={`screen-${i}`} className="k-hist__line k-hist__line--now">
                {line === '' ? ' ' : line}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  )
}
